/**
 * Maps Firebase auth error codes to text we are willing to show a user.
 *
 * Shared by the sign-in panel and the profile pages. It exists because the
 * alternative — reaching for libs/mfa's mfaErrorMessage — has an MFA-flavoured
 * fallback ("two-factor authentication isn't available on your account yet"),
 * which is actively misleading when what actually failed was, say, an email
 * change. Use mfaErrorMessage only for enrol/unenrol/challenge operations, and
 * this for everything else.
 *
 * Never render `err.message`. The compat layer installs Firebase's debugErrorMap
 * (@firebase/auth-compat/dist/index.cjs.js), so `.message` is a developer string
 * with a docs URL in it. Two existing pages get this wrong — UpdateEmail and
 * UpdatePassword both surface `err.message` directly — and this module exists so
 * the sign-in path does not repeat it.
 *
 * Returning null means "say nothing": some outcomes are the visitor's own choice
 * and an error banner would be noise.
 */
var MESSAGES = {
    'auth/invalid-email': "That doesn't look like a valid email address.",
    // Deliberately identical. Firebase's Email Enumeration Protection collapses
    // these into auth/invalid-credential anyway, and telling an attacker which
    // half was wrong is exactly what that feature exists to prevent. Keeping the
    // three in step means the copy does not change if the setting is toggled.
    'auth/user-not-found': "We couldn't find an account with that email and password.",
    'auth/wrong-password': "We couldn't find an account with that email and password.",
    'auth/invalid-credential': "We couldn't find an account with that email and password.",
    'auth/user-disabled': 'This account has been disabled. Contact support@ltiaas.com.',
    'auth/too-many-requests': 'Too many attempts. Wait a few minutes, or reset your password.',
    'auth/network-request-failed': "We couldn't reach the sign-in service. Check your connection and try again.",
    'auth/email-already-in-use': 'An account already exists for that email. Sign in instead.',
    'auth/weak-password': 'Choose a password of at least 6 characters.',
    'auth/missing-password': 'Enter your password.',
    'auth/unauthorized-domain': "This address isn't authorised for sign-in. Contact support@ltiaas.com.",
    'auth/popup-blocked': 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.',
    // Both of these mean the visitor closed the popup or opened a second one.
    // Nothing went wrong, so say nothing.
    'auth/popup-closed-by-user': null,
    'auth/cancelled-popup-request': null,
    'auth/user-cancelled': "You didn't finish granting access, so we couldn't sign you in.",
    'auth/invalid-verification-code': "That code wasn't accepted. Codes change every 30 seconds — try the current one.",
    'auth/missing-verification-code': 'Enter the 6-digit code from your authenticator app.',
    'auth/invalid-multi-factor-session': 'Your sign-in attempt expired. Please start again.',
    'auth/missing-multi-factor-session': 'Your sign-in attempt expired. Please start again.',
    'auth/multi-factor-info-not-found': 'That authenticator is no longer enrolled on this account. Contact support@ltiaas.com.',
    'auth/requires-recent-login': 'For security, please sign out and sign back in, then try again.',
    // Email changes. updateEmail is deprecated and is rejected outright when email
    // enumeration protection is on (the default for projects created since
    // September 2023, and for anything upgraded to Identity Platform), so this is
    // reachable if any caller still uses it.
    // Covers both "this sign-in method is off" and "updateEmail is rejected because
    // email enumeration protection is on".
    'auth/operation-not-allowed': "That isn't enabled for this project. Please contact support@ltiaas.com.",
    'auth/email-change-needs-verification': 'Please verify your current email address before changing it.',
    'auth/invalid-new-email': "That doesn't look like a valid email address.",
    'auth/missing-new-email': 'Enter the new email address.',
};
var FALLBACK = 'Something went wrong signing you in. Try again, or contact support@ltiaas.com.';
/** True when this code has a deliberate message (including a deliberate silence). */
export var isKnownAuthError = function (code) {
    return !!code && Object.prototype.hasOwnProperty.call(MESSAGES, code);
};
/**
 * @returns the message to show, or null to show nothing.
 */
export var authErrorMessage = function (code) {
    if (!code) {
        return FALLBACK;
    }
    if (Object.prototype.hasOwnProperty.call(MESSAGES, code)) {
        return MESSAGES[code];
    }
    return FALLBACK;
};
